"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EMPTY_SUMMARY = void 0;
exports.caseNodeHeight = caseNodeHeight;
exports.isExpandedByDepth = isExpandedByDepth;
exports.pluralPl = pluralPl;
exports.layout = layout;
const typesTreeModel_1 = require("./typesTreeModel");
const DEPTH_RANK = { milestones: 1, cases: 2, subCases: 3 };
const NOTHING = { visible: 0, total: 0 };
exports.EMPTY_SUMMARY = {
    contractTypes: NOTHING,
    milestoneTypes: NOTHING,
    caseTypes: NOTHING,
    subCaseTypes: NOTHING,
    tasks: NOTHING,
};
// Szerszy odstęp między kolumnami niż same prostokąty: linie mają wtedy dłuższy,
// łagodniejszy łuk i widać, które dziecko należy do którego rodzica.
const COL_X = [0, 330, 660, 1010];
const NODE_W = [230, 230, 260, 230];
const NODE_H = 42;
// Wiersz zadania i pasek nad listą (kreska + oddech). Rozwinięte zadania rosną
// wyłącznie w PIONIE - szerokość kolumn jest nietknięta, bo to ona decyduje,
// czy drzewo mieści się na laptopie.
const TASK_ROW_H = 20;
const TASK_BLOCK_PAD = 4;
const GAP_Y = 10;
const GROUP_GAP = 16;
const PAD = 20;
/**
 * Współrzędna `y` rodzica, przy której jego nagłówek stanie w połowie między
 * skrajnymi dziećmi. Liczone po punktach zaczepienia, więc lista zadań w kaflu
 * dziecka nie przeciąga rodzica w dół. Null dla zbioru pustego.
 */
function centerOf(nodes) {
    if (!nodes.length)
        return null;
    const top = Math.min(...nodes.map((node) => node.anchorY));
    const bottom = Math.max(...nodes.map((node) => node.anchorY));
    return (top + bottom) / 2 - NODE_H / 2;
}
/** Wysokość kafla typu sprawy: nagłówek plus rozwinięta lista zadań. */
function caseNodeHeight(taskCount, showTasks) {
    if (!showTasks || taskCount === 0)
        return NODE_H;
    return NODE_H + TASK_BLOCK_PAD + taskCount * TASK_ROW_H;
}
/**
 * Czy dzieci węzła są widoczne przy danej głębokości, bez wyjątków lokalnych.
 *
 * Potrzebne widokowi: chevron przywrócony do stanu zgodnego z poziomem przestaje
 * być wyjątkiem i ma zniknąć z listy wyjątków, inaczej „Zresetuj widok" świeciłby
 * się mimo braku czegokolwiek do zresetowania.
 */
function isExpandedByDepth(kind, depth) {
    if (kind === "milestoneType")
        return DEPTH_RANK[depth] >= 2;
    if (kind === "caseType")
        return DEPTH_RANK[depth] >= 3;
    return false;
}
/** Polska odmiana po liczbie - liczniki i podsumowanie mówią zdaniem, nie skrótem. */
function pluralPl(count, one, few, many) {
    if (count === 1)
        return one;
    const lastDigit = count % 10;
    const lastTwo = count % 100;
    if (lastDigit >= 2 && lastDigit <= 4 && (lastTwo < 12 || lastTwo > 14))
        return few;
    return many;
}
function layout(data, selectedContractTypeId, options = {}) {
    const showTasks = options.showTasks ?? true;
    const depthRank = DEPTH_RANK[options.depth ?? "subCases"];
    const overrides = options.expanded ?? {};
    const contractType = data.contractTypes.find((type) => type.id === selectedContractTypeId);
    if (!contractType)
        return { nodes: [], edges: [], width: 0, height: 0, summary: exports.EMPTY_SUMMARY };
    /**
     * Pełna gałąź policzona z danych, ZANIM cokolwiek zostanie ukryte.
     *
     * Liczniki ukrytych dzieci i podsumowanie muszą znać stan pełny - inaczej
     * zwinięcie chowałoby także dowód na to, że coś schowano.
     */
    const branches = (0, typesTreeModel_1.milestoneTypesForContractType)(data, contractType.id).map(({ edge, milestoneType }) => ({
        edge,
        milestoneType,
        cases: (0, typesTreeModel_1.caseTypesForMilestoneType)(data, milestoneType.id).map((caseType) => ({
            caseType,
            subCaseTypes: (0, typesTreeModel_1.subCaseTypesFor)(data, caseType.id),
        })),
    }));
    /** Wyjątek lokalny bije głębokość; bez wyjątku decyduje ona. */
    const isExpanded = (nodeId, rankNeeded) => overrides[nodeId] ?? depthRank >= rankNeeded;
    const nodes = [];
    const edges = [];
    const nextFree = [PAD, PAD, PAD, PAD];
    function makeNode(kind, column, entityId, label, preferredY, extra = {}) {
        const y = Math.max(preferredY ?? nextFree[column], nextFree[column]);
        // Wysokość bierzemy z węzła, nie ze stałej: kafel sprawy rośnie o listę
        // zadań, a kolejny kafel ma zacząć się POD nim, nie 42 px niżej.
        const h = extra.h ?? NODE_H;
        const node = {
            id: `${kind}:${entityId}`,
            kind,
            entityId,
            label,
            x: COL_X[column],
            y,
            w: NODE_W[column],
            h,
            anchorY: y + NODE_H / 2,
            ...extra,
        };
        nodes.push(node);
        nextFree[column] = y + h + GAP_Y;
        return node;
    }
    /** Zadania w kaflu - ta sama reguła dla obu ról typu sprawy, żeby żadna nie chowała ich po cichu. */
    function taskExtra(caseType) {
        const tasks = (0, typesTreeModel_1.taskTemplatesFor)(caseType);
        return {
            taskCount: tasks.length,
            tasks: showTasks ? tasks.map(toLayoutTask) : undefined,
            h: caseNodeHeight(tasks.length, showTasks),
        };
    }
    /** Licznik ukrytych dzieci wraz ze zdaniem do podpowiedzi. Pusty, gdy nic nie ukryto. */
    function hiddenExtra(count, one, few, many) {
        if (count <= 0)
            return {};
        return { hiddenCount: count, hiddenLabel: `Ukryte: ${count} ${pluralPl(count, one, few, many)}` };
    }
    /* ---------- przebieg 1: kamienie i sprawy ---------- */
    const milestoneNodes = [];
    /** Identyfikatory węzłów spraw zakładanych automatycznie razem z kamieniem. */
    const defaultCaseNodeIds = new Set();
    /** Węzły oznaczone jako domyślne, ale bez szablonu - nie powstaną. */
    const gapNodeIds = new Set();
    /** subCaseTypeId -> WIDOCZNE węzły spraw, pod którymi jest dopuszczony. */
    const parentsOfSubCase = new Map();
    /** subCaseTypeId -> dane typu, potrzebne przy tworzeniu węzła w przebiegu 2. */
    const subCaseTypeById = new Map();
    for (const branch of branches) {
        const milestoneNodeId = `milestoneType:${branch.milestoneType.id}`;
        const casesVisible = isExpanded(milestoneNodeId, 2);
        const caseNodes = [];
        if (casesVisible) {
            for (const { caseType, subCaseTypes } of branch.cases) {
                const caseNodeId = `caseType:${caseType.id}`;
                const subCasesVisible = isExpanded(caseNodeId, 3);
                const caseNode = makeNode("caseType", 2, caseType.id, caseType.name, null, {
                    badge: caseType.folderNumber ?? undefined,
                    description: caseType.description,
                    ...taskExtra(caseType),
                    canCollapse: subCaseTypes.length > 0,
                    isExpanded: subCasesVisible,
                    ...hiddenExtra(subCasesVisible ? 0 : subCaseTypes.length, "typ podsprawy", "typy podspraw", "typów podspraw"),
                });
                caseNodes.push(caseNode);
                // Flagę „domyślny” typu sprawy niesie krawędź z kamienia - tak samo jak
                // przy kamieniach. Dzięki temu jednym rzutem oka widać, które sprawy
                // powstają automatycznie przy zakładaniu umowy.
                if ((0, typesTreeModel_1.isCreatedAutomatically)(caseType))
                    defaultCaseNodeIds.add(caseNode.id);
                if ((0, typesTreeModel_1.hasTemplateGap)(caseType))
                    gapNodeIds.add(caseNode.id);
                if (!subCasesVisible)
                    continue;
                for (const subCaseType of subCaseTypes) {
                    subCaseTypeById.set(subCaseType.id, subCaseType);
                    const parents = parentsOfSubCase.get(subCaseType.id) ?? [];
                    parents.push(caseNode);
                    parentsOfSubCase.set(subCaseType.id, parents);
                }
            }
            // Odstęp między gałęziami, żeby sprawy jednego kamienia nie zlewały się
            // wizualnie ze sprawami następnego. Tylko gdy gałąź coś narysowała -
            // pusta gałąź nie ma prawa spychać następnej w dół.
            if (caseNodes.length)
                nextFree[2] += GROUP_GAP;
        }
        const milestoneNode = makeNode("milestoneType", 1, branch.milestoneType.id, branch.milestoneType.name, centerOf(caseNodes), {
            description: branch.milestoneType.description,
            hasTemplate: branch.milestoneType._templateId !== null,
            canCollapse: branch.cases.length > 0,
            isExpanded: casesVisible,
            ...hiddenExtra(casesVisible ? 0 : branch.cases.length, "typ sprawy", "typy spraw", "typów spraw"),
        });
        milestoneNodes.push(milestoneNode);
        caseNodes.forEach((caseNode) => edges.push({
            fromId: milestoneNode.id,
            toId: caseNode.id,
            isDefault: defaultCaseNodeIds.has(caseNode.id),
            hasGap: gapNodeIds.has(caseNode.id),
        }));
    }
    /* ---------- przebieg 2: podsprawy, przy swoich rodzicach ---------- */
    // Kolejność wg docelowej wysokości, żeby przycinanie do wolnego miejsca
    // nie poprzestawiało węzłów względem siebie.
    const subCaseOrder = Array.from(parentsOfSubCase.entries())
        .map(([subCaseTypeId, parents]) => ({ subCaseTypeId, parents, desiredY: centerOf(parents) ?? 0 }))
        .sort((a, b) => a.desiredY - b.desiredY);
    for (const { subCaseTypeId, parents, desiredY } of subCaseOrder) {
        const subCaseType = subCaseTypeById.get(subCaseTypeId);
        if (!subCaseType)
            continue;
        // Typ dopuszczony i jako zwykła sprawa, i jako podsprawa dostaje prostokąt
        // w OBU kolumnach - to nie jest duplikat, tylko dwie różne role tego samego
        // typu. Identyfikatory węzłów są rozłączne (caseType: / subCaseType:),
        // więc zaznaczenie i edycja działają na każdym z osobna.
        //
        // Podsprawa dzielona między rodzicami zostaje na ekranie, dopóki choć jeden
        // rodzic jest rozwinięty. Zwinięci rodzice policzyli ją już u siebie.
        const subCaseNode = makeNode("subCaseType", 3, subCaseType.id, subCaseType.name, desiredY, {
            badge: subCaseType.folderNumber ?? undefined,
            isSubCaseOnly: subCaseType.isSubCaseOnly,
            description: subCaseType.description,
            ...taskExtra(subCaseType),
        });
        parents.forEach((parent) => edges.push({ fromId: parent.id, toId: subCaseNode.id }));
    }
    /* ---------- pusta gałąź: typ umowy bez kamieni ---------- */
    // Bez tego kafla widok wygląda na zepsuty. Dotychczasowy komunikat pustego
    // stanu pokazywał się tylko przy CAŁKIEM pustym płótnie, a od kolumny zerowej
    // płótno puste nigdy nie jest - stoi na nim lista wszystkich typów umów.
    const emptyBranchNode = branches.length
        ? null
        : makeNode("emptyBranch", 1, contractType.id, "brak przypisanych kamieni", null);
    /* ---------- kolumna zerowa: wszystkie typy umów ---------- */
    // Kolejność bierzemy z danych i niczego nie sortujemy: to ta sama lista, którą
    // widok pokazywał do tej pory w kolumnie po lewej (backend oddaje ją alfabetycznie).
    // Przestawienie jej przy okazji przebudowy układu byłoby zmianą, o którą nikt nie prosił.
    const contractTypes = data.contractTypes;
    const selectedIndex = contractTypes.findIndex((type) => type.id === contractType.id);
    const CONTRACT_PITCH = NODE_H + GAP_Y;
    // Wybrany typ stoi naprzeciwko środka SWOJEJ gałęzi - jak każdy inny rodzic
    // w tym drzewie. Przy pustej gałęzi naprzeciwko kafla „brak kamieni".
    const anchorNodes = milestoneNodes.length ? milestoneNodes : emptyBranchNode ? [emptyBranchNode] : [];
    const selectedY = centerOf(anchorNodes) ?? PAD;
    const firstContractY = selectedY - selectedIndex * CONTRACT_PITCH;
    // Gdy lista wyjdzie nad płótno, w dół przesuwa się CAŁOŚĆ, a nie sama kolumna -
    // inaczej wybrany typ przestałby stać naprzeciwko swojej gałęzi.
    const shift = Math.max(0, PAD - firstContractY);
    const contractNodes = contractTypes.map((type, index) => {
        const isCurrent = type.id === contractType.id;
        // Odstęp jest jeden dla całej listy, także wokół wybranego typu: przerwa
        // w kolumnie czytałaby się jako „tu czegoś brakuje".
        const y = firstContractY + index * CONTRACT_PITCH;
        const node = {
            id: `contractType:${type.id}`,
            kind: "contractType",
            entityId: type.id,
            label: type.name,
            description: type.description,
            // E2: sam znacznik przekreślenia, bez plakietki. Pełne słowo wraca
            // w podpowiedzi kafla i w pasku narzędzi nad drzewem.
            isRetired: type.status === "OLD",
            isDimmed: !isCurrent,
            x: COL_X[0],
            y,
            w: NODE_W[0],
            h: NODE_H,
            anchorY: y + NODE_H / 2,
            // Nierozwinięty typ niesie licznik kamieni TYM SAMYM mechanizmem, co
            // każdy inny zwinięty kafel - nic nie znika bez śladu.
            ...(isCurrent
                ? {}
                : hiddenExtra((0, typesTreeModel_1.milestoneTypesForContractType)(data, type.id).length, "typ kamienia", "typy kamieni", "typów kamieni")),
        };
        nodes.push(node);
        return node;
    });
    const contractNode = contractNodes[selectedIndex];
    if (emptyBranchNode)
        edges.push({ fromId: contractNode.id, toId: emptyBranchNode.id });
    milestoneNodes.forEach((milestoneNode) => {
        const edge = data.contractTypeMilestoneTypes.find((candidate) => candidate.contractTypeId === contractType.id &&
            candidate.milestoneTypeId === milestoneNode.entityId);
        edges.push({
            fromId: contractNode.id,
            toId: milestoneNode.id,
            label: edge?.folderNumber ?? undefined,
            // Zielona linia znaczy „powstaje samo", a to wymaga flagi I szablonu.
            isDefault: !!edge?.isDefault && milestoneNode.hasTemplate,
            hasGap: !!edge?.isDefault && !milestoneNode.hasTemplate,
        });
    });
    // Kolumna typów umów bywa wyższa od gałęzi i wtedy wychodzi nad płótno.
    // Przesuwamy wszystko naraz, żeby geometria względna została nietknięta.
    if (shift > 0)
        nodes.forEach((node) => {
            node.y += shift;
            node.anchorY += shift;
        });
    // Szerokość liczona z faktycznie użytych kolumn - gdy nie ma podspraw, nie
    // rezerwujemy pustego pasa po prawej, dzięki czemu graf da się wyśrodkować.
    // Przy zwiniętym drzewie działa to tak samo: nieużyta kolumna nie zajmuje miejsca.
    const width = Math.max(...nodes.map((node) => node.x + node.w)) + PAD;
    const height = Math.max(...nodes.map((node) => node.y + node.h)) + PAD;
    return {
        nodes,
        edges,
        width,
        height,
        summary: summarize(branches, nodes, contractTypes.length),
    };
}
/**
 * Ile z czego widać i ile jest w całej gałęzi wybranego typu umowy.
 *
 * Zadania liczone po KAFLACH, nie po typach: ten sam typ stojący w dwóch rolach ma
 * dwa kafle i pokazuje swoje zadania w obu, więc licząc po typach zdanie nad drzewem
 * rozjeżdżałoby się z tym, co widać na ekranie.
 */
function summarize(branches, nodes, contractTypeCount) {
    const allCases = branches.flatMap((branch) => branch.cases);
    const allSubCaseTypes = new Map();
    allCases.forEach(({ subCaseTypes }) => subCaseTypes.forEach((subCaseType) => allSubCaseTypes.set(subCaseType.id, subCaseType)));
    const visibleOf = (kind) => nodes.filter((node) => node.kind === kind).length;
    const taskCountOf = (caseType) => (0, typesTreeModel_1.taskTemplatesFor)(caseType).length;
    return {
        // Typy umów są w kolumnie zerowej WSZYSTKIE, więc te dwie liczby są równe.
        // Zdanie nad drzewem i tak musi je wymienić - inaczej przemilczałoby kolumnę,
        // która zajmuje jedną czwartą szerokości widoku.
        contractTypes: { visible: visibleOf("contractType"), total: contractTypeCount },
        milestoneTypes: { visible: visibleOf("milestoneType"), total: branches.length },
        caseTypes: { visible: visibleOf("caseType"), total: allCases.length },
        subCaseTypes: { visible: visibleOf("subCaseType"), total: allSubCaseTypes.size },
        tasks: {
            visible: nodes.reduce((sum, node) => sum + (node.tasks?.length ?? 0), 0),
            total: allCases.reduce((sum, { caseType }) => sum + taskCountOf(caseType), 0) +
                Array.from(allSubCaseTypes.values()).reduce((sum, subCaseType) => sum + taskCountOf(subCaseType), 0),
        },
    };
}
function toLayoutTask(task) {
    return { id: task.id, name: task.name, status: task.status };
}
